// src/context/ReviewContext.js
"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const ReviewContext = createContext();
export const useReview = () => useContext(ReviewContext);

const REVIEWS_API_URL = `${BASE_URL}/reviews`;
const PRODUCTS_API_URL = `${BASE_URL}/products`;

export const ReviewProvider = ({ children }) => {
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { token, adminId, isReady } = useAuth();

  /* --------------------------------
     AUTH HEADER
  -------------------------------- */
  const getAuthHeader = useCallback(
    (isFormData = false) => {
      if (!token) return {};

      return {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        ...(isFormData
          ? {}
          : { "Content-Type": "application/json" }),
      };
    },
    [token]
  );

  const debug = (...args) => {
    if (process.env.NODE_ENV !== "production") {
      console.debug(...args);
    }
  };

  /* --------------------------------
     FETCH REVIEWS
  -------------------------------- */
  const fetchReviews = async () => {
    setError(null);
    setLoading(true);

    try {
      debug("Fetching reviews with token:", token);

      const response = await fetch(REVIEWS_API_URL, {
        method: "GET",
        headers: getAuthHeader(),
      });

      const data = await response.json();

      debug("Reviews response:", data);

      if (
        data?.status === "200" &&
        Array.isArray(data.data)
      ) {
        setReviews(data.data);
      } else {
        setReviews([]);
      }
    } catch (err) {
      console.error("Fetch reviews error:", err);
      setError(err.message);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------
     FETCH PRODUCTS
  -------------------------------- */
  const fetchProducts = async () => {
    try {
      const response = await fetch(PRODUCTS_API_URL, {
        method: "GET",
        headers: getAuthHeader(),
      });

      const data = await response.json();

      console.log("Products response:", data);

      if (Array.isArray(data?.data)) {
        setProducts(data.data);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error("Fetch products error:", err);
      setError(err.message);
    }
  };

  /* --------------------------------
     ADD REVIEW
  -------------------------------- */
  const addReview = async (formData) => {
    console.log("addReview called with:", formData);

    try {
      const response = await fetch(REVIEWS_API_URL, {
        method: "POST",
        headers: getAuthHeader(true),
        body: formData,
      });

      const data = await response.json();

      console.log("Add review response:", data);

      await fetchReviews();

      return data;
    } catch (err) {
      console.error("Add review error:", err);
      setError(err.message);
      throw err;
    }
  };

  /* --------------------------------
     UPDATE REVIEW
  -------------------------------- */
  const updateReview = async (id, formData) => {
    try {
      formData.append("_method", "PUT");

      const response = await fetch(
        `${REVIEWS_API_URL}/${id}`,
        {
          method: "POST",
          headers: getAuthHeader(true),
          body: formData,
        }
      );

      const data = await response.json();

      await fetchReviews();

      return data;
    } catch (err) {
      console.error("Update review error:", err);
      setError(err.message);
      throw err;
    }
  };

  /* --------------------------------
     TOGGLE REVIEW STATUS
  -------------------------------- */
  const toggleReviewStatus = async (
    id,
    currentStatus
  ) => {
    const newStatus =
      currentStatus === "Active"
        ? "Inactive"
        : "Active";

    try {
      await fetch(
        `${REVIEWS_API_URL}/${id}/status`,
        {
          method: "PATCH",
          headers: getAuthHeader(),
          body: JSON.stringify({
            status: newStatus,
            update_audit_id: adminId || null,
          }),
        }
      );

      setReviews((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, status: newStatus }
            : r
        )
      );
    } catch (err) {
      setError(err.message);
    }
  };

  /* --------------------------------
     DELETE REVIEW
  -------------------------------- */
  const deleteReview = async (id) => {
    try {
      await fetch(`${REVIEWS_API_URL}/${id}`, {
        method: "DELETE",
        headers: getAuthHeader(),
        body: JSON.stringify({
          delete_audit_id: adminId || null,
        }),
      });

      setReviews((prev) =>
        prev.filter((r) => r.id !== id)
      );
    } catch (err) {
      console.error("Delete review error:", err);
      setError(err.message);
    }
  };

  /* --------------------------------
     AUTO LOAD ON AUTH READY
  -------------------------------- */
  useEffect(() => {
    debug("Auth state:", {
      token,
      adminId,
      isReady,
    });

    if (!isReady) return;

    if (!token) {
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);

      try {
        await Promise.all([
          fetchReviews(),
          fetchProducts(),
        ]);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, isReady]);

  return (
    <ReviewContext.Provider
      value={{
        reviews,
        products,
        loading,
        error,
        addReview,
        toggleReviewStatus,
        deleteReview,
        updateReview,
        refetch: fetchReviews,
      }}
    >
      {children}
    </ReviewContext.Provider>
  );
};